import type * as React from 'react';
import type {Strategy as FloatingStrategy} from '@floating-ui/react';
import type {Placement} from '@react-types/overlays';
import {createViewConfig} from '@sail/engine';
import type {View} from '../view';
import type {Layer} from './layers/Layer';

type DelayOptions = {show?: number; hide?: number};

export type TooltipProps = View.IntrinsicElement<
  'div',
  {
    /**
     * The element or DOM ref used to open and position the Tooltip.
     *
     * Tooltip attaches event listeners directly to the trigger’s DOM element,
     * and modifies some of its attributes to ensure accessibility, like `className`, `tabindex` and `aria-describedby`.
     */
    trigger: React.ReactElement | React.RefObject<HTMLElement | SVGElement>;

    /**
     * How the tooltip should be positioned relative to the trigger element.
     * */
    floatingStrategy?: FloatingStrategy;

    /**
     * How the Tooltip should be placed relative to the trigger element.
     *
     * @default 'bottom'
     */
    placement: Placement;

    /**
     * Array of placements to try if the initial placement doesn't fit on the axes in which overflow is checked.
     *
     * If `placement` is set to `'top'` and `fallbackPlacements` is set to `['bottom', 'left']`, the placements to try
     * (in order) are `['top', 'bottom', 'left']`.
     */
    fallbackPlacements?: Placement[];

    /**
     * How much time in milliseconds to wait before showing the Tooltip on hover.
     */
    delay?: number | DelayOptions;

    /**
     * The style variant of the Tooltip.
     *
     * @default 'description'
     */
    type?: 'description' | 'label';

    /**
     * The content to place inside of the Tooltip.
     */
    children?: React.ReactNode;

    /** The available subviews */
    subviews?: View.Subviews<{layer: typeof Layer}>;
  }
>;

export const TooltipConfig = createViewConfig({
  props: {} as TooltipProps,
  name: 'Tooltip',
  defaults: {
    type: 'description',
    placement: 'bottom',
  },
});
